import { useState } from "react";
import { App, Modal, Form, Button, notification, Flex } from "antd";
import dayjs from "dayjs";
import TaskTemplateForm from "@/components/forms/TaskTemplateForm";
import {
  createItem as createTemplate,
  updateItem as updateTemplate,
} from "@/api/task-templates";
import InfoCircleTooltip from "@/components/ui/InfoCircleTooltip";
import routes from "@/routes";
import { useNavigate } from "react-router-dom";

function buildPayload(v) {
  return {
    template_title: v.template_title ?? "",

    task_uuid: v.task_uuid ?? null,

    name: v.name ?? "",
    description: v.description ?? "",
    parent_task_uuid: v.parent_task_uuid ?? null,

    unit_uuid: v.unit_uuid ?? null,

    start_date: v.start_date ? dayjs(v.start_date).format("YYYY-MM-DD") : null,
    due_date: v.due_date ? dayjs(v.due_date).toISOString() : null,

    responsible_executor_uuid: v.responsible_executor_uuid ?? null,
    assignee_acceptor_uuid: v.assignee_acceptor_uuid ?? null,

    importance_uuid: v.importance_uuid ?? null,

    coexecutors_uuids: Array.isArray(v.coexecutors_uuids)
      ? v.coexecutors_uuids
      : [],

    is_accessible_for_all_in_unit: !!v.is_accessible_for_all_in_unit,

    is_visible: v.is_visible ?? true,

    planned_costs: v.planned_costs ?? null,
    require_actuals_on_acceptance: v.require_actuals_on_acceptance ?? false,
    task_type_uuid: v.task_type_uuid ?? null,
  };
}

function TaskTemplatesModal({
  isAdmins = false,
  data,
  task = "",
  statuses = [],
  priorities = [],
  taskTypes = [],
  units = [],
  visible,
  onSave,
  onCancel,
}) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const { message } = App.useApp();

  const isEdit = !!data?.uuid;

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);
      setIsLoading(true);

      let saved;
      if (isEdit) {
        saved = await updateTemplate({
          uuid: data.uuid,
          ...payload,
          name: payload.name || payload.template_title,
        });

        const tplNumber = saved?.template_code ?? "";
        const tplName = saved?.template_title ?? "";

        notification.open({
          message: (
            <div style={{ fontWeight: 500 }}>
              Обновлен шаблон задачи: {tplNumber ? `№${tplNumber} - ` : ""}
              {tplName}
            </div>
          ),
          description: null,
          icon: null,
          duration: 4,
          pauseOnHover: true,
          placement: "bottomRight",
          style: {
            border: "1px solid #B7EB8F",
            background: "#f6ffed",
            borderRadius: "8px",
            width: "500px",
          },
        });
      } else {
        saved = await createTemplate({
          ...payload,
          name: payload.name || payload.template_title,
        });

        const tplNumber = saved?.template_code ?? "";
        const tplName = saved?.template_title ?? "";

        notification.open({
          message: (
            <div style={{ fontWeight: 500 }}>
              Создан шаблон задачи: {tplNumber ? `№${tplNumber} - ` : ""}
              {tplName}
            </div>
          ),
          description: (
            <Button
              type="link"
              style={{
                background: "transparent",
                border: "1px solid #D5E2F7",
                color: "#0066a6",
                fontWeight: 500,
              }}
              onClick={() =>
                navigate(
                  isAdmins
                    ? routes.administration.taskTemplateDetail(saved.uuid)
                    : routes.taskTemplateDetail(saved.uuid)
                )
              }
            >
              Перейти к шаблону
            </Button>
          ),
          icon: null,
          duration: 4,
          pauseOnHover: true,
          placement: "bottomRight",
          style: {
            border: "1px solid #B7EB8F",
            background: "#f6ffed",
            borderRadius: "8px",
            width: "500px",
          },
        });
      }

      onSave?.(saved);
      form.resetFields();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.message || "Не удалось сохранить шаблон");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel?.();
  };

  return (
    <Modal
      maskClosable={false}
      title={
        <Flex align="center" gap={12}>
          <span>{isEdit ? "Редактирование шаблона" : "Новый шаблон"}</span>

          <InfoCircleTooltip placement="top" size={20}>
            Добавление или изменение ресурсов, файлов, критериев и других
            параметров возможно только после создания шаблона.
          </InfoCircleTooltip>
        </Flex>
      }
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={isEdit ? "Сохранить" : "Создать"}
      cancelText="Отмена"
      okButtonProps={{ loading: isLoading }}
      width={640}
      bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Отмена
        </Button>,
        <Button key="ok" type="primary" loading={isLoading} onClick={handleOk}>
          {isEdit ? "Сохранить" : "Создать"}
        </Button>,
      ]}
    >
      <TaskTemplateForm
        task={task}
        form={form}
        isAdmins={isAdmins}
        data={data}
        statuses={statuses}
        priorities={priorities}
        taskTypes={taskTypes}
        units={units}
      />
    </Modal>
  );
}

export default TaskTemplatesModal;
